import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Switch,
  Card,
  CardContent,
  Grid,
  Paper,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Stack,
  Container
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Add,
  Delete,
  ArrowUpward,
  ArrowDownward,
  CloudUpload,
  Palette,
  Share,
  Instagram,
  Twitter,
  YouTube,
  Email,
  Language,
  WhatsApp,
  LocalCafe
} from '@mui/icons-material';
import { API_BASE_URL } from '../config/api';

interface LinkItem {
  id: string;
  label: string;
  url: string;
  isActive: boolean;
  order: number;
}

interface SocialLinks {
  instagram: string;
  twitter: string;
  tiktok: string;
  youtube: string;
  website: string;
  email: string;
  whatsapp: string;
}

interface LinktreeData {
  title: string;
  description: string;
  logoUrl: string;
  theme: 'coffee' | 'dark' | 'glassmorphism' | 'sunset' | 'custom';
  customTheme?: {
    backgroundColor: string;
    buttonColor: string;
    buttonTextColor: string;
    textColor: string;
  };
  links: LinkItem[];
  socialLinks: SocialLinks;
}

const LinktreeManagementPage: React.FC = () => {
  const navigate = useNavigate();

  const getLinkIcon = (url: string, label: string) => {
    const lowercaseUrl = (url || '').toLowerCase();
    const lowercaseLabel = (label || '').toLowerCase();

    const iconStyle = { marginRight: '8px', fontSize: 18, flexShrink: 0 };

    if (
      lowercaseUrl.includes('apple.com') ||
      lowercaseUrl.includes('apps.apple') ||
      lowercaseLabel.includes('app store') ||
      lowercaseLabel.includes('apple') ||
      lowercaseLabel.includes('ios')
    ) {
      return (
        <svg viewBox="0 0 384 512" fill="currentColor" style={{ width: '16px', height: '16px', marginRight: '8px', flexShrink: 0 }}>
          <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-48-20.1-77.5-19.6-38.6.6-74.1 22.4-93.8 56.4-39.9 69-10.2 172.7 28.5 228.5 18.9 27.2 41.2 57.6 70.7 56.5 28.1-1.1 38.8-18.1 70.8-18.1 32 0 41.8 18.1 70.8 17.6 30-1.1 49.6-27.7 68.3-55 21.7-31.7 30.6-62.5 31-64.2-1-.4-59.5-22.8-60.1-88zm-52.7-185.3c15.2-18.2 25.5-43.6 22.7-68.9-21.7 1-48.4 14.6-63.9 32.7-13.8 15.8-25.8 41.5-22.5 66.2 24.2 1.9 48.9-11.8 63.7-30z"/>
        </svg>
      );
    }

    if (
      lowercaseUrl.includes('play.google.com') ||
      lowercaseLabel.includes('google play') ||
      lowercaseLabel.includes('play store') ||
      lowercaseLabel.includes('android')
    ) {
      return (
        <svg viewBox="0 0 512 512" fill="currentColor" style={{ width: '16px', height: '16px', marginRight: '8px', flexShrink: 0 }}>
          <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58 33.3-60.1-60.1L472.5 220c8.2-4.7 8.2-16.7-.3-21.4zM325.3 277.7l60.1 60.1L104.6 499l220.7-221.3z"/>
        </svg>
      );
    }

    if (lowercaseUrl.includes('wa.me') || lowercaseUrl.includes('whatsapp') || lowercaseLabel.includes('whatsapp')) {
      return <WhatsApp style={iconStyle} />;
    }

    if (lowercaseUrl.includes('instagram.com') || lowercaseLabel.includes('instagram') || lowercaseLabel.includes('insta')) {
      return <Instagram style={iconStyle} />;
    }

    if (lowercaseUrl.includes('tiktok.com') || lowercaseLabel.includes('tiktok')) {
      return (
        <svg viewBox="0 0 448 512" fill="currentColor" style={{ width: '16px', height: '16px', marginRight: '8px', flexShrink: 0 }}>
          <path d="M448 209.91a210.06 210.06 0 0 1-122.77-39.25v178.72A162.55 162.55 0 1 1 185 188.31v89.89a74.62 74.62 0 1 0 52.23 71.52V0h88a121.18 121.18 0 0 0 1.86 22.17A122.18 122.18 0 0 0 381 102.39a121.43 121.43 0 0 0 67 20.14z"/>
        </svg>
      );
    }

    if (lowercaseLabel.includes('web') || lowercaseLabel.includes('site') || lowercaseUrl.includes('http')) {
      return <Language style={iconStyle} />;
    }

    return <LocalCafe style={iconStyle} />;
  };

  const [data, setData] = useState<LinktreeData>({
    title: 'SobCof',
    description: 'Kahve, Arkadaşlık ve Eğlence! ☕️',
    logoUrl: '',
    theme: 'coffee',
    customTheme: {
      backgroundColor: '#1e0f08',
      buttonColor: '#8B4513',
      buttonTextColor: '#FFFFFF',
      textColor: '#DEB887'
    },
    links: [],
    socialLinks: {
      instagram: '',
      twitter: '',
      tiktok: '',
      youtube: '',
      website: '',
      email: '',
      whatsapp: ''
    }
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [analytics, setAnalytics] = useState({
    views: 0,
    totalClicks: 0,
    clicks: {} as Record<string, number>,
    socialClicks: {} as Record<string, number>
  });

  useEffect(() => {
    fetchLinktreeSettings();
  }, []);

  const fetchLinktreeSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      const [settingsRes, analyticsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/links`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/admin/links/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(err => {
          console.error('Error fetching analytics:', err);
          return { data: { success: true, data: { views: 0, totalClicks: 0, clicks: {}, socialClicks: {} } } };
        })
      ]);

      if (settingsRes.data.success) {
        const responseData = settingsRes.data.data;
        setData({
          ...responseData,
          customTheme: responseData.customTheme || {
            backgroundColor: '#1e0f08',
            buttonColor: '#8B4513',
            buttonTextColor: '#FFFFFF',
            textColor: '#DEB887'
          },
          links: (responseData.links || []).sort((a: LinkItem, b: LinkItem) => a.order - b.order),
          socialLinks: {
            instagram: '',
            twitter: '',
            tiktok: '',
            youtube: '',
            website: '',
            email: '',
            whatsapp: '',
            ...(responseData.socialLinks || {})
          }
        });
      }

      if (analyticsRes.data.success) {
        const rawData = analyticsRes.data.data || {};
        setAnalytics({
          views: rawData.views || 0,
          totalClicks: rawData.totalClicks || 0,
          clicks: rawData.clicks || {},
          socialClicks: rawData.socialClicks || {}
        });
      }
    } catch (error) {
      console.error('Error fetching Linktree settings:', error);
      setErrorMessage('Ayarlar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setSuccessMessage('');
      setErrorMessage('');
      const token = localStorage.getItem('adminToken');
      const response = await axios.put(`${API_BASE_URL}/api/admin/links`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setSuccessMessage('Linktree ayarları başarıyla kaydedildi!');
        setTimeout(() => setSuccessMessage(''), 4000);
      }
    } catch (error) {
      console.error('Error saving Linktree settings:', error);
      setErrorMessage('Ayarlar kaydedilirken hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // File validation
    if (!file.type.startsWith('image/')) {
      alert('Lütfen geçerli bir resim dosyası seçin.');
      return;
    }

    try {
      setUploading(true);
      setErrorMessage('');
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('logo', file);

      const response = await axios.post(`${API_BASE_URL}/api/admin/links/upload-logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setData(prev => ({
          ...prev,
          logoUrl: response.data.logoUrl
        }));
        setSuccessMessage('Logo başarıyla yüklendi.');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      setErrorMessage('Logo yüklenirken hata oluştu.');
    } finally {
      setUploading(false);
    }
  };

  // Links List operations
  const handleAddLink = () => {
    const newLink: LinkItem = {
      id: Math.random().toString(36).substring(2),
      label: 'Yeni Bağlantı',
      url: 'https://',
      isActive: true,
      order: data.links.length + 1
    };

    setData(prev => ({
      ...prev,
      links: [...prev.links, newLink]
    }));
  };

  const handleRemoveLink = (id: string) => {
    setData(prev => {
      const filtered = prev.links.filter(l => l.id !== id);
      // Re-normalize ordering
      const updated = filtered.map((link, idx) => ({
        ...link,
        order: idx + 1
      }));
      return {
        ...prev,
        links: updated
      };
    });
  };

  const handleLinkFieldChange = (id: string, field: keyof LinkItem, value: any) => {
    setData(prev => {
      const updated = prev.links.map(l => {
        if (l.id === id) {
          return { ...l, [field]: value };
        }
        return l;
      });
      return {
        ...prev,
        links: updated
      };
    });
  };

  const moveLink = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= data.links.length) return;

    setData(prev => {
      const linksCopy = [...prev.links];
      // Swap elements
      const temp = linksCopy[index];
      linksCopy[index] = linksCopy[targetIndex];
      linksCopy[targetIndex] = temp;

      // Re-assign orders
      const updated = linksCopy.map((link, idx) => ({
        ...link,
        order: idx + 1
      }));

      return {
        ...prev,
        links: updated
      };
    });
  };

  // Social Links operations
  const handleSocialFieldChange = (field: keyof SocialLinks, value: string) => {
    setData(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [field]: value
      }
    }));
  };

  // Preset Theme Style details for Live Preview background
  const getThemeBackgroundStyles = () => {
    switch (data.theme) {
      case 'coffee':
        return {
          background: 'linear-gradient(180deg, #1A0A00 0%, #0D0500 100%)',
          color: '#FFFFFF'
        };
      case 'dark':
        return {
          background: 'radial-gradient(circle at top, #0f172a 0%, #020617 100%)',
          color: '#94a3b8'
        };
      case 'glassmorphism':
        return {
          background: 'linear-gradient(-45deg, #ee7752, #e73c7e, #23a6d5, #23d5ab)',
          backgroundSize: '200% 200%',
          color: '#ffffff'
        };
      case 'sunset':
        return {
          background: 'linear-gradient(180deg, #ff7e5f 0%, #feb47b 100%)',
          color: '#4a2f26'
        };
      case 'custom':
        return {
          backgroundColor: data.customTheme?.backgroundColor || '#111827',
          color: data.customTheme?.textColor || '#ffffff'
        };
      default:
        return {};
    }
  };

  const getThemeButtonStyles = () => {
    switch (data.theme) {
      case 'coffee':
        return {
          background: 'linear-gradient(135deg, #D4845A 0%, #8B4513 100%)',
          color: '#FFFFFF',
          border: '1px solid rgba(212, 132, 90, 0.3)'
        };
      case 'dark':
        return {
          background: 'rgba(30, 41, 59, 0.7)',
          color: '#38bdf8',
          border: '1px solid rgba(56, 189, 248, 0.2)'
        };
      case 'glassmorphism':
        return {
          background: 'rgba(255, 255, 255, 0.15)',
          color: '#ffffff',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          backdropFilter: 'blur(8px)'
        };
      case 'sunset':
        return {
          background: '#ffffff',
          color: '#ff6a00',
          border: '1px solid rgba(255, 255, 255, 0.8)'
        };
      case 'custom':
        return {
          backgroundColor: data.customTheme?.buttonColor || '#1f2937',
          color: data.customTheme?.buttonTextColor || '#ffffff',
          border: '1px solid rgba(255, 255, 255, 0.08)'
        };
      default:
        return {};
    }
  };

  const renderSocialIconPreview = (key: string, value: string) => {
    if (!value || value.trim() === '') return null;
    let icon = <Language fontSize="small" />;
    if (key === 'instagram') icon = <Instagram fontSize="small" />;
    if (key === 'twitter') icon = <Twitter fontSize="small" />;
    if (key === 'youtube') icon = <YouTube fontSize="small" />;
    if (key === 'email') icon = <Email fontSize="small" />;
    if (key === 'whatsapp') icon = <WhatsApp fontSize="small" />;
    if (key === 'tiktok') {
      icon = (
        <svg viewBox="0 0 448 512" fill="currentColor" style={{ width: '14px', height: '14px' }}>
          <path d="M448 209.91a210.06 210.06 0 0 1-122.77-39.25v178.72A162.55 162.55 0 1 1 185 188.31v89.89a74.62 74.62 0 1 0 52.23 71.52V0h88a121.18 121.18 0 0 0 1.86 22.17A122.18 122.18 0 0 0 381 102.39a121.43 121.43 0 0 0 67 20.14z"/>
        </svg>
      );
    }

    return (
      <Box
        key={key}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.1)',
          border: '1px solid rgba(255,255,255,0.15)',
          color: 'inherit'
        }}
      >
        {icon}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#f8fafc' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: '#8B4513', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">Ayarlar yükleniyor...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header App Bar */}
      <AppBar position="sticky" sx={{ bgcolor: '#8B4513', boxShadow: 'none' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/admin/dashboard')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Share sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Linktree Yönetimi
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
            onClick={handleSaveSettings}
            disabled={saving || uploading}
            sx={{
              bgcolor: '#D2691E',
              '&:hover': { bgcolor: '#CD853F' },
              px: 3,
              borderRadius: 2
            }}
          >
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Status Alerts */}
        {successMessage && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{successMessage}</Alert>}
        {errorMessage && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{errorMessage}</Alert>}

        <Grid container spacing={4}>
          {/* LEFT COLUMN: EDIT FORM */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <Stack spacing={3}>
              {/* Card 0: Analytics Dashboard */}
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#8B4513', mb: 3 }}>
                    📊 Linktree Analizleri
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid size={{ xs: 4 }}>
                      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: '#fcf8f5', borderColor: '#f0e3db' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Sayfa Ziyareti</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, mt: 1, color: '#8B4513' }}>{analytics.views}</Typography>
                      </Paper>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: '#fcf8f5', borderColor: '#f0e3db' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Toplam Tıklama</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, mt: 1, color: '#8B4513' }}>{analytics.totalClicks}</Typography>
                      </Paper>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: '#fcf8f5', borderColor: '#f0e3db' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Tıklama Oranı (CTR)</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, mt: 1, color: '#D2691E' }}>
                          {analytics.views > 0 ? `${((analytics.totalClicks / analytics.views) * 100).toFixed(1)}%` : '0%'}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#4a2f26' }}>
                    Buton Tıklama Dağılımı
                  </Typography>
                  <Stack spacing={1} sx={{ mb: 3 }}>
                    {data.links.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>Henüz buton eklenmemiş.</Typography>
                    ) : (
                      data.links.map((link) => {
                        const clickCount = (analytics.clicks || {})[link.id] || 0;
                        const percentage = analytics.totalClicks > 0 ? (clickCount / analytics.totalClicks) * 100 : 0;
                        return (
                          <Box key={link.id} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                              {getLinkIcon(link.url, link.label)}
                              <Typography variant="body2" sx={{ fontWeight: 600, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', ml: 1 }}>
                                {link.label || 'Başlıksız Buton'}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#8B4513' }}>{clickCount} tık</Typography>
                              <Typography variant="caption" color="text.secondary">({percentage.toFixed(0)}%)</Typography>
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </Stack>

                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#4a2f26' }}>
                    Sosyal Medya İkon Tıklamaları
                  </Typography>
                  <Grid container spacing={1}>
                    {Object.entries(data.socialLinks).filter(([_, val]) => val && val.trim() !== '').length === 0 ? (
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>Aktif sosyal medya ikonu yok.</Typography>
                      </Grid>
                    ) : (
                      Object.entries(data.socialLinks)
                        .filter(([_, val]) => val && val.trim() !== '')
                        .map(([key, _]) => {
                          const clickCount = (analytics.socialClicks || {})[key] || 0;
                          return (
                            <Grid key={key} size={{ xs: 6, sm: 4 }}>
                              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                                  {key}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#D2691E' }}>
                                  {clickCount} tık
                                </Typography>
                              </Box>
                            </Grid>
                          );
                        })
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* Card 1: General Info */}
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#8B4513', mb: 3 }}>
                    📝 Genel Bilgiler
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <Box sx={{ position: 'relative', mb: 2 }}>
                        {data.logoUrl ? (
                          <img
                            src={data.logoUrl}
                            alt="Logo Preview"
                            style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', border: '3px solid #8B4513' }}
                          />
                        ) : (
                          <Box sx={{ width: 100, height: 100, borderRadius: '50%', bgcolor: '#f1f5f9', border: '2px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CloudUpload sx={{ fontSize: 32, color: '#94a3b8' }} />
                          </Box>
                        )}
                        {uploading && (
                          <Box sx={{ position: 'absolute', top: 0, left: 0, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress size={30} sx={{ color: '#fff' }} />
                          </Box>
                        )}
                      </Box>
                      <Button
                        variant="outlined"
                        component="label"
                        size="small"
                        disabled={uploading}
                        startIcon={<CloudUpload />}
                        sx={{ color: '#8B4513', borderColor: '#8B4513', '&:hover': { borderColor: '#CD853F', bgcolor: 'rgba(139,69,19,0.05)' } }}
                      >
                        Logo Yükle
                        <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
                      </Button>
                    </Grid>

                    <Grid size={{ xs: 12, md: 8 }}>
                      <Stack spacing={3}>
                        <TextField
                          fullWidth
                          label="Profil Başlığı"
                          value={data.title}
                          onChange={(e) => setData({ ...data, title: e.target.value })}
                          placeholder="Örn: SobCof Cafe"
                        />
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Açıklama / Alt Başlık"
                          value={data.description}
                          onChange={(e) => setData({ ...data, description: e.target.value })}
                          placeholder="Profilinizde görünecek kısa açıklama yazısı..."
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Card 2: Theme Selection */}
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#8B4513', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Palette /> Tema Seçimi
                  </Typography>

                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="theme-select-label">Hazır Tema Preseti</InputLabel>
                    <Select
                      labelId="theme-select-label"
                      value={data.theme}
                      label="Hazır Tema Preseti"
                      onChange={(e) => setData({ ...data, theme: e.target.value as any })}
                    >
                      <MenuItem value="coffee">☕️ Coffee (Kahve Tonları)</MenuItem>
                      <MenuItem value="dark">🌑 Dark Sleek (Göz Alıcı Karanlık)</MenuItem>
                      <MenuItem value="glassmorphism">✨ Glassmorphic (Buzlu Cam Efekti)</MenuItem>
                      <MenuItem value="sunset">🌅 Sunset Gradient (Gün Batımı)</MenuItem>
                      <MenuItem value="custom">🎨 Özel Tema Renkleri</MenuItem>
                    </Select>
                  </FormControl>

                  {data.theme === 'custom' && (
                    <Paper sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#475569' }}>
                        Özel Renk Ayarları
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            label="Arka Plan Rengi"
                            type="color"
                            value={data.customTheme?.backgroundColor || '#111827'}
                            onChange={(e) => setData({
                              ...data,
                              customTheme: { ...(data.customTheme || { backgroundColor: '#111827', buttonColor: '#1f2937', buttonTextColor: '#ffffff', textColor: '#ffffff' }), backgroundColor: e.target.value }
                            })}
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& input': { height: '38px', padding: '4px' } }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            label="Yazı Rengi"
                            type="color"
                            value={data.customTheme?.textColor || '#ffffff'}
                            onChange={(e) => setData({
                              ...data,
                              customTheme: { ...(data.customTheme || { backgroundColor: '#111827', buttonColor: '#1f2937', buttonTextColor: '#ffffff', textColor: '#ffffff' }), textColor: e.target.value }
                            })}
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& input': { height: '38px', padding: '4px' } }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            label="Buton Arka Plan Rengi"
                            type="color"
                            value={data.customTheme?.buttonColor || '#1f2937'}
                            onChange={(e) => setData({
                              ...data,
                              customTheme: { ...(data.customTheme || { backgroundColor: '#111827', buttonColor: '#1f2937', buttonTextColor: '#ffffff', textColor: '#ffffff' }), buttonColor: e.target.value }
                            })}
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& input': { height: '38px', padding: '4px' } }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            label="Buton Yazı Rengi"
                            type="color"
                            value={data.customTheme?.buttonTextColor || '#ffffff'}
                            onChange={(e) => setData({
                              ...data,
                              customTheme: { ...(data.customTheme || { backgroundColor: '#111827', buttonColor: '#1f2937', buttonTextColor: '#ffffff', textColor: '#ffffff' }), buttonTextColor: e.target.value }
                            })}
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& input': { height: '38px', padding: '4px' } }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  )}
                </CardContent>
              </Card>

              {/* Card 3: Social Media Accounts */}
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#8B4513', mb: 3 }}>
                    📲 Sosyal Medya İkonları
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Doldurduğunuz sosyal ağlar sayfanın altında ikon olarak belirecektir. Boş bıraktıklarınız görünmez.
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Instagram Kullanıcı Adı"
                        value={data.socialLinks.instagram}
                        onChange={(e) => handleSocialFieldChange('instagram', e.target.value)}
                        placeholder="sobcof"
                        InputProps={{
                          startAdornment: <Instagram sx={{ color: 'text.secondary', mr: 1 }} />
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="X (Twitter) Kullanıcı Adı"
                        value={data.socialLinks.twitter}
                        onChange={(e) => handleSocialFieldChange('twitter', e.target.value)}
                        placeholder="sobcof"
                        InputProps={{
                          startAdornment: <Twitter sx={{ color: 'text.secondary', mr: 1 }} />
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="TikTok Kullanıcı Adı"
                        value={data.socialLinks.tiktok}
                        onChange={(e) => handleSocialFieldChange('tiktok', e.target.value)}
                        placeholder="sobcof"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="YouTube Kanalı veya Linki"
                        value={data.socialLinks.youtube}
                        onChange={(e) => handleSocialFieldChange('youtube', e.target.value)}
                        placeholder="channel/sobcof"
                        InputProps={{
                          startAdornment: <YouTube sx={{ color: 'text.secondary', mr: 1 }} />
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Web Sitesi URL"
                        value={data.socialLinks.website}
                        onChange={(e) => handleSocialFieldChange('website', e.target.value)}
                        placeholder="https://sobcof.com"
                        InputProps={{
                          startAdornment: <Language sx={{ color: 'text.secondary', mr: 1 }} />
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="E-posta Adresi"
                        value={data.socialLinks.email}
                        onChange={(e) => handleSocialFieldChange('email', e.target.value)}
                        placeholder="sobcof@gmail.com"
                        InputProps={{
                          startAdornment: <Email sx={{ color: 'text.secondary', mr: 1 }} />
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="WhatsApp Numarası (Ülke kodu ile birlikte)"
                        value={data.socialLinks.whatsapp}
                        onChange={(e) => handleSocialFieldChange('whatsapp', e.target.value)}
                        placeholder="905XXXXXXXXX"
                        helperText="Örn: Başında '+' olmadan ülke kodunu ekleyin (905551234567)"
                        InputProps={{
                          startAdornment: <WhatsApp sx={{ color: 'text.secondary', mr: 1 }} />
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Card 4: Links Manager */}
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#8B4513' }}>
                      🔗 Linkler ve Butonlar
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={handleAddLink}
                      sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#CD853F' } }}
                    >
                      Yeni Buton Ekle
                    </Button>
                  </Box>

                  {data.links.length === 0 ? (
                    <Box sx={{ py: 6, textStyle: 'italic', color: 'text.secondary', textAlign: 'center', border: '1px dashed #cbd5e1', borderRadius: 2 }}>
                      Hiç link eklenmedi. "Yeni Buton Ekle" butonuna basarak ilk bağlantınızı ekleyin.
                    </Box>
                  ) : (
                    <Stack spacing={2}>
                      {data.links.map((link, index) => (
                        <Paper
                          key={link.id}
                          elevation={0}
                          sx={{
                            p: 2,
                            border: '1px solid #e2e8f0',
                            borderRadius: 2,
                            bgcolor: link.isActive ? 'white' : '#f8fafc',
                            position: 'relative'
                          }}
                        >
                          <Grid container spacing={2} alignItems="center">
                            {/* Drag / Sort handles */}
                            <Grid size={{ xs: 1.5, sm: 1 }} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <IconButton
                                size="small"
                                disabled={index === 0}
                                onClick={() => moveLink(index, 'up')}
                                title="Yukarı Taşı"
                              >
                                <ArrowUpward fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                disabled={index === data.links.length - 1}
                                onClick={() => moveLink(index, 'down')}
                                title="Aşağı Taşı"
                              >
                                <ArrowDownward fontSize="small" />
                              </IconButton>
                            </Grid>

                            {/* Inputs fields */}
                            <Grid size={{ xs: 10.5, sm: 7.5 }}>
                              <Stack spacing={1.5}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Buton Metni"
                                  value={link.label}
                                  onChange={(e) => handleLinkFieldChange(link.id, 'label', e.target.value)}
                                  placeholder="Örn: Android Uygulaması"
                                />
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Bağlantı URL'i"
                                  value={link.url}
                                  onChange={(e) => handleLinkFieldChange(link.id, 'url', e.target.value)}
                                  placeholder="https://"
                                />
                              </Stack>
                            </Grid>

                            {/* Toggles & Delete button */}
                            <Grid size={{ xs: 12, sm: 3.5 }} sx={{ display: 'flex', justifyContent: { xs: 'space-between', sm: 'flex-end' }, alignItems: 'center', gap: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="caption" sx={{ mr: 1, color: link.isActive ? 'success.main' : 'text.secondary', fontWeight: 600 }}>
                                  {link.isActive ? 'Aktif' : 'Pasif'}
                                </Typography>
                                <Switch
                                  size="small"
                                  checked={link.isActive}
                                  onChange={(e) => handleLinkFieldChange(link.id, 'isActive', e.target.checked)}
                                  color="success"
                                />
                              </Box>
                              <IconButton
                                color="error"
                                onClick={() => handleRemoveLink(link.id)}
                                title="Sil"
                                sx={{ ml: 1 }}
                              >
                                <Delete />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          {/* RIGHT COLUMN: INTERACTIVE LIVE MOBILE PREVIEW */}
          <Grid size={{ xs: 12, lg: 5 }} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                position: 'sticky',
                top: 100,
                width: '100%',
                maxWidth: 380,
                height: 720,
                bgcolor: '#0f172a', // Smartphone border color
                borderRadius: '45px',
                p: '15px',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                border: '6px solid #334155',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Notch */}
              <Box sx={{ width: 140, height: 25, bgcolor: '#334155', borderBottomLeftRadius: 15, borderBottomRightRadius: 15, mx: 'auto', mt: -1, mb: 1, zIndex: 10, display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#0f172a', mt: 0.8, mr: 1 }} />
                <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: '#0f172a', mt: 1 }} />
              </Box>

              {/* Dynamic Screen Container */}
              <Box
                sx={{
                  flex: 1,
                  borderRadius: '32px',
                  overflowY: 'auto',
                  position: 'relative',
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  transition: 'all 0.4s ease',
                  ...getThemeBackgroundStyles(),
                  // Custom theme variables support
                  ...(data.theme === 'custom' && data.customTheme ? {
                    '--custom-bg': data.customTheme.backgroundColor,
                    '--custom-text': data.customTheme.textColor,
                    '--custom-btn-bg': data.customTheme.buttonColor,
                    '--custom-btn-text': data.customTheme.buttonTextColor,
                  } : {})
                }}
              >
                {/* Profile Pic Preview */}
                <Box sx={{ mt: 3, mb: 2, position: 'relative' }}>
                  <Box
                    component="img"
                    src={data.logoUrl || '/assets/LogoWhite.png'}
                    alt="Logo Preview"
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '3px solid rgba(255,255,255,0.8)',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      padding: data.logoUrl ? 0 : '10px',
                      boxSizing: 'border-box'
                    }}
                  />
                </Box>

                {/* Title Preview */}
                <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center', width: '100%' }}>
                  {data.title && (
                    data.title.endsWith('.png') ||
                    data.title.endsWith('.jpg') ||
                    data.title.endsWith('.jpeg') ||
                    data.title.endsWith('.svg') ||
                    data.title.startsWith('/assets/') ||
                    data.title.startsWith('http')
                  ) ? (
                    <Box
                      component="img"
                      src={data.title}
                      alt="Profile Title Logo"
                      sx={{ maxHeight: 36, width: 'auto', objectFit: 'contain' }}
                    />
                  ) : (
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'center', color: 'inherit' }}>
                      {data.title || 'SobCof'}
                    </Typography>
                  )}
                </Box>

                {/* Description Preview */}
                <Typography variant="caption" sx={{ opacity: 0.85, mb: 4, textAlign: 'center', px: 2, lineHeight: 1.4, color: 'inherit' }}>
                  {data.description || 'Açıklama girilmedi.'}
                </Typography>

                {/* Active Links Buttons Preview */}
                <Stack spacing={1.5} sx={{ width: '100%', mb: 4 }}>
                  {data.links.filter(l => l.isActive).length === 0 ? (
                    <Typography variant="caption" sx={{ fontStyle: 'italic', opacity: 0.5, textAlign: 'center' }}>
                      Henüz aktif buton yok
                    </Typography>
                  ) : (
                    data.links
                      .filter(l => l.isActive)
                      .map((link) => (
                        <Box
                          key={link.id}
                          sx={{
                            width: '100%',
                            py: 1.4,
                            px: 2,
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600,
                            fontSize: '13px',
                            cursor: 'pointer',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                            transition: 'transform 0.2s',
                            boxSizing: 'border-box',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            '&:hover': { transform: 'scale(1.02)' },
                            ...getThemeButtonStyles()
                          }}
                        >
                          {getLinkIcon(link.url, link.label)}
                          <span>{link.label || 'Başlıksız Buton'}</span>
                        </Box>
                      ))
                  )}
                </Stack>

                {/* Social icons Preview */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    mt: 'auto',
                    width: '100%',
                    pt: 2,
                    color: 'inherit'
                  }}
                >
                  {Object.entries(data.socialLinks).map(([key, val]) =>
                    renderSocialIconPreview(key, val as string)
                  )}
                </Box>

                {/* Footer Brand */}
                <Typography variant="caption" sx={{ mt: 3, opacity: 0.4, fontSize: '9px', letterSpacing: '0.5px' }}>
                  Powered by SobCof
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LinktreeManagementPage;
